import type { DesignModeGroup, DesignModeSelection, DesignModeValue, StoryDesignModeSettings } from './types';

export type ResolvedDesignModeValue<T> = {
	value: T;
	source: 'base' | 'override';
	condition?: DesignModeSelection;
};

const ID = /^[a-z][a-z0-9-]*$/;

export const DEFAULT_DESIGN_MODE_GROUPS: DesignModeGroup[] = [
	{
		id: 'theme',
		name: 'Theme',
		defaultModeId: 'light',
		modes: [
			{ id: 'light', name: 'Light', colorScheme: 'light' },
			{ id: 'dark', name: 'Dark', colorScheme: 'dark' },
		],
	},
];

export const DEFAULT_STORY_DESIGN_MODES: StoryDesignModeSettings = {
	activation: {
		theme: { type: 'fixed', modeId: 'light' },
	},
};

export const AUTO_STORY_DESIGN_MODES: StoryDesignModeSettings = {
	activation: {
		theme: {
			type: 'media',
			feature: 'prefers-color-scheme',
			mapping: { light: 'light', dark: 'dark' },
		},
	},
};

export function createDesignModeValue<T>(base: T): DesignModeValue<T> {
	return { base };
}

export function copyDesignModeGroups(groups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS): DesignModeGroup[] {
	return groups.map(group => ({ ...group, modes: group.modes.map(mode => ({ ...mode })) }));
}

export function validateDesignModeGroups(input: unknown): DesignModeGroup[] {
	if (!Array.isArray(input) || !input.length) throw new Error('A design definition requires mode groups.');
	const groupIds = new Set<string>();
	return input.map(rawGroup => {
		const group = rawGroup as DesignModeGroup;
		if (
			!group ||
			typeof group.id !== 'string' ||
			!ID.test(group.id) ||
			groupIds.has(group.id) ||
			typeof group.name !== 'string' ||
			!group.name.trim() ||
			!Array.isArray(group.modes) ||
			!group.modes.length
		)
			throw new Error('Mode groups require unique valid IDs, names and modes.');
		groupIds.add(group.id);
		const modeIds = new Set<string>();
		const modes = group.modes.map(rawMode => {
			const mode = rawMode;
			if (
				!mode ||
				typeof mode.id !== 'string' ||
				!ID.test(mode.id) ||
				modeIds.has(mode.id) ||
				typeof mode.name !== 'string' ||
				!mode.name.trim() ||
				(mode.colorScheme !== undefined && mode.colorScheme !== 'light' && mode.colorScheme !== 'dark')
			)
				throw new Error('Modes require unique valid IDs, names and optional color schemes.');
			modeIds.add(mode.id);
			return {
				id: mode.id,
				name: mode.name,
				...(mode.colorScheme ? { colorScheme: mode.colorScheme } : {}),
			};
		});
		if (!modeIds.has(group.defaultModeId)) throw new Error(`Unknown default mode: ${group.defaultModeId}.`);
		return { id: group.id, name: group.name, defaultModeId: group.defaultModeId, modes };
	});
}

function conditionsOverlap(left: DesignModeSelection, right: DesignModeSelection): boolean {
	return Object.keys(left).every(groupId => right[groupId] === undefined || right[groupId] === left[groupId]);
}

export function validateDesignModeSelection(
	selection: unknown,
	groups: DesignModeGroup[],
	{ allowEmpty = false }: { allowEmpty?: boolean } = {}
): DesignModeSelection {
	if (!selection || typeof selection !== 'object' || Array.isArray(selection))
		throw new Error('A mode selection must be an object.');
	const entries = Object.entries(selection as DesignModeSelection);
	if (!allowEmpty && !entries.length) throw new Error('A mode condition must select at least one mode.');
	return Object.fromEntries(
		entries.map(([groupId, modeId]) => {
			const group = groups.find(item => item.id === groupId);
			if (!group || !group.modes.some(mode => mode.id === modeId))
				throw new Error(`Unknown design mode selection: ${groupId}=${modeId}.`);
			return [groupId, modeId];
		})
	);
}

export function validateDesignModeValue<T>(
	input: unknown,
	groups: DesignModeGroup[],
	normalize: (value: unknown) => T
): DesignModeValue<T> {
	if (!input || typeof input !== 'object' || Array.isArray(input) || !('base' in input))
		throw new Error('A mode value requires a base value.');
	const value = input as DesignModeValue<unknown>;
	if (value.overrides !== undefined && !Array.isArray(value.overrides))
		throw new Error('Mode value overrides must be an array.');
	const overrides = (value.overrides ?? []).map(override => {
		if (!override || typeof override !== 'object' || !('value' in override))
			throw new Error('A mode override requires a condition and value.');
		return {
			when: validateDesignModeSelection(override.when, groups),
			value: normalize(override.value),
		};
	});
	for (let left = 0; left < overrides.length; left += 1) {
		for (let right = left + 1; right < overrides.length; right += 1) {
			if (
				Object.keys(overrides[left].when).length === Object.keys(overrides[right].when).length &&
				conditionsOverlap(overrides[left].when, overrides[right].when)
			)
				throw new Error('Mode overrides with equal specificity must not overlap.');
		}
	}
	return {
		base: normalize(value.base),
		...(overrides.length ? { overrides } : {}),
	};
}

export function getDefaultDesignModeSelection(groups: DesignModeGroup[]): DesignModeSelection {
	return Object.fromEntries(groups.map(group => [group.id, group.defaultModeId]));
}

export function resolveStoryDesignModeSelection(
	groups: DesignModeGroup[],
	settings: StoryDesignModeSettings | undefined,
	media: { prefersDark: boolean }
): DesignModeSelection {
	const selection = getDefaultDesignModeSelection(groups);
	for (const group of groups) {
		const activation = settings?.activation[group.id];
		if (activation?.type === 'fixed') selection[group.id] = activation.modeId;
		if (activation?.type === 'media' && activation.feature === 'prefers-color-scheme') {
			selection[group.id] = activation.mapping[media.prefersDark ? 'dark' : 'light'];
		}
	}
	return selection;
}

export function resolveDesignModeValueWithSource<T>(
	value: DesignModeValue<T>,
	selection: DesignModeSelection
): ResolvedDesignModeValue<T> {
	const matches = (value.overrides ?? []).filter(override =>
		Object.entries(override.when).every(([groupId, modeId]) => selection[groupId] === modeId)
	);
	if (!matches.length) return { value: value.base, source: 'base' };
	const resolved = matches.reduce((best, current) =>
		Object.keys(current.when).length > Object.keys(best.when).length ? current : best
	);
	return { value: resolved.value, source: 'override', condition: resolved.when };
}

export function resolveDesignModeValue<T>(value: DesignModeValue<T>, selection: DesignModeSelection): T {
	return resolveDesignModeValueWithSource(value, selection).value;
}

export function getDesignModeOverrideCondition(
	selection: DesignModeSelection,
	groups: DesignModeGroup[]
): DesignModeSelection {
	const defaultSelection = getDefaultDesignModeSelection(groups);
	return Object.fromEntries(
		groups
			.map(group => [group.id, selection[group.id] ?? group.defaultModeId] as const)
			.filter(([groupId, modeId]) => defaultSelection[groupId] !== modeId)
	);
}

function sameDesignModeCondition(left: DesignModeSelection, right: DesignModeSelection): boolean {
	const leftEntries = Object.entries(left);
	return (
		leftEntries.length === Object.keys(right).length &&
		leftEntries.every(([groupId, modeId]) => right[groupId] === modeId)
	);
}

export function getExactDesignModeOverride<T>(
	value: DesignModeValue<T>,
	selection: DesignModeSelection,
	groups: DesignModeGroup[]
) {
	const condition = getDesignModeOverrideCondition(selection, groups);
	if (!Object.keys(condition).length) return undefined;
	return value.overrides?.find(override => sameDesignModeCondition(override.when, condition));
}

export function removeExactDesignModeOverride<T>(
	value: DesignModeValue<T>,
	selection: DesignModeSelection,
	groups: DesignModeGroup[]
): DesignModeValue<T> {
	const condition = getDesignModeOverrideCondition(selection, groups);
	if (!Object.keys(condition).length || !value.overrides?.length) return value;
	const overrides = value.overrides.filter(override => !sameDesignModeCondition(override.when, condition));
	if (overrides.length === value.overrides.length) return value;
	if (overrides.length) return { ...value, overrides };
	return { base: value.base };
}

export function setDesignModeValue<T>(
	value: DesignModeValue<T>,
	selection: DesignModeSelection,
	nextValue: T,
	groups: DesignModeGroup[]
): DesignModeValue<T> {
	const condition = getDesignModeOverrideCondition(selection, groups);
	if (!Object.keys(condition).length) return { ...value, base: nextValue };

	const overrides = [...(value.overrides ?? [])];
	const existingIndex = overrides.findIndex(override => sameDesignModeCondition(override.when, condition));
	const nextOverride = { when: condition, value: nextValue };
	if (existingIndex === -1) overrides.push(nextOverride);
	else overrides[existingIndex] = nextOverride;
	return { ...value, overrides };
}

export function mergeDesignModeSelection(
	parent: DesignModeSelection,
	override?: DesignModeSelection
): DesignModeSelection {
	return override ? { ...parent, ...override } : parent;
}
